import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as Tooltip from '@radix-ui/react-tooltip'
import {
  Plus, UserSquare2, Sparkles, Trash2, Pencil, FileText, Loader2, TriangleAlert,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, Maximize2, RotateCcw,
} from 'lucide-react'
import { api, mediaURL } from '../api'
import type { Avatar, Look, Role } from '../types'
import { atLeast } from '../types'
import { AuthImage, FilePicker } from '../components'
import { go } from '../router'
import { useGeneration, type LookResult, type Task } from '../tasks'
import {
  Button, Card, CardTitle, ConfirmButton, EmptyState, Field, Input, Menu, Textarea,
  SectionLabel, Step, ErrorText, Dialog } from '../components/ui'
import { Select, Tabs } from '../components/select'
import { VoicePicker } from '../components/VoicePicker'
import { PhoneImageUpload } from '../components/PhoneImageUpload'

const genderOptions = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
]

const ageRangeOptions = [
  { value: '18_24', label: '18–24' },
  { value: '25_34', label: '25–34' },
  { value: '35_44', label: '35–44' },
  { value: '45_54', label: '45–54' },
  { value: '55_plus', label: '55+' },
]

type LookTaskInput = {
  avatar_id: string
  env_key: string
  environment: string
  name: string
  outfit: string
  props: string
  extra: string
  mic: string
  space: string
  ambience: string
  camera_style: 'fixed' | 'handheld' | 'match_frame'
  speaking_to: 'camera' | 'off_camera' | 'match_frame'
  posture: 'seated' | 'standing' | 'match_frame'
}

type LookTask = Task<LookResult, LookTaskInput>
type LookConcept = { id: string; group: string; title: string; description: string }

export function Avatars({ ws, sub, role }: { ws: string; sub?: 'new'; role?: Role }) {
  const qc = useQueryClient()
  // The form is a URL, not a boolean. It survives a reload, the back button
  // leaves it, and cancelling is a navigation you can undo -- none of which is
  // true of component state.
  //
  // Nothing here is guarded any more. It used to ask before you left, because
  // leaving discarded a plate that had been paid for; creating an avatar now
  // generates nothing, so the most a stray click costs is retyping a name.
  const adding = sub === 'new'
  const open = () => go({ name: 'settings', section: 'avatars', sub: 'new' })
  const close = () => go({ name: 'settings', section: 'avatars' })

  const avatars = useQuery({
    queryKey: ['avatars', ws],
    queryFn: () => api.get<Avatar[]>(`/api/workspaces/${ws}/avatars`),
  })
  const canEdit = atLeast(role, 'member')

  return (
    <div className="space-y-5">
      {canEdit && (
        <div className="flex justify-end">
          {!adding && <Button variant="primary" onClick={open}><Plus /> New avatar</Button>}
        </div>
      )}

      {adding && (
        <NewAvatar
          ws={ws}
          onDone={() => { close(); qc.invalidateQueries({ queryKey: ['avatars', ws] }) }}
          onClose={close}
        />
      )}

      <ErrorText error={avatars.error} />

      {avatars.data?.length === 0 && !adding && (
        <EmptyState
          icon={UserSquare2}
          title="No avatars yet"
          body="One ordinary photo of a person is the whole avatar — nothing is generated to create one, and nothing is spent."
          action={canEdit
            ? <Button variant="primary" onClick={open}><Plus /> New avatar</Button>
            : undefined}
        />
      )}

      <div className="grid gap-5">
        {avatars.data?.map(a => <AvatarCard key={a.id} ws={ws} avatar={a} canEdit={canEdit} />)}
      </div>
    </div>
  )
}

function AvatarCard({ ws, avatar, canEdit }: { ws: string; avatar: Avatar; canEdit: boolean }) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [addLook, setAddLook] = useState(false)
  const [editingDetails, setEditingDetails] = useState(false)
  const [promptLook, setPromptLook] = useState<Look | null>(null)
  const [avatarPromptOpen, setAvatarPromptOpen] = useState(false)
  const [viewLookID, setViewLookID] = useState<string | null>(null)
  const looks = useQuery({
    queryKey: ['looks', ws, avatar.id],
    queryFn: () => api.get<Look[]>(`/api/workspaces/${ws}/avatars/${avatar.id}/looks`),
  })
  const tasks = useQuery({
    queryKey: ['tasks', ws],
    queryFn: () => api.get<LookTask[]>(`/api/workspaces/${ws}/tasks`),
    refetchInterval: query => {
      const current = query.state.data as LookTask[] | undefined
      return current?.some(task => (task.kind === 'generate_look' || task.kind === 'clean_avatar')
        && (task.status === 'queued' || task.status === 'running')) ? 1500 : false
    },
  })

  const avatarTasks = (tasks.data ?? []).filter(task =>
    task.kind === 'generate_look' && task.input.avatar_id === avatar.id)
  const activeLookTasks = avatarTasks.filter(task => task.status === 'queued' || task.status === 'running')
  const failedLookTasks = avatarTasks.filter(task => task.status === 'failed')
  const cleanupTask = (tasks.data ?? []).find(task =>
    task.kind === 'clean_avatar' && task.input.avatar_id === avatar.id &&
    (task.status === 'queued' || task.status === 'running'))
  const completedCleanupIDs = (tasks.data ?? []).filter(task =>
    task.kind === 'clean_avatar' && task.input.avatar_id === avatar.id && task.status === 'done')
    .map(task => task.id).sort().join(',')
  const completedTaskIDs = avatarTasks
    .filter(task => task.status === 'done')
    .map(task => task.id)
    .sort()
    .join(',')

  // The worker creates the look row before marking the task done. Refreshing
  // here swaps the durable placeholder for the saved image, including after
  // this page has been reloaded while generation was running.
  useEffect(() => {
    if (completedTaskIDs) {
      qc.invalidateQueries({ queryKey: ['looks', ws, avatar.id] })
    }
  }, [avatar.id, completedTaskIDs, qc, ws])

  useEffect(() => {
    if (completedCleanupIDs) qc.invalidateQueries({ queryKey: ['avatars', ws] })
  }, [completedCleanupIDs, qc, ws])

  const remove = useMutation({
    mutationFn: () => api.del(`/api/workspaces/${ws}/avatars/${avatar.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['avatars', ws] }),
  })

  const lookCount = looks.data?.length ?? 0
  const pendingCount = activeLookTasks.length
  const savedLooks = looks.data ?? []
  const viewedLookIndex = savedLooks.findIndex(look => look.id === viewLookID)
  const viewedLook = viewedLookIndex >= 0 ? savedLooks[viewedLookIndex] : null

  const moveViewer = (direction: -1 | 1) => {
    if (viewedLookIndex < 0 || savedLooks.length < 2) return
    const nextIndex = (viewedLookIndex + direction + savedLooks.length) % savedLooks.length
    setViewLookID(savedLooks[nextIndex].id)
  }

  const downloadLook = async (look: Look) => {
    const url = await mediaURL(ws, look.start_frame_key)
    const extension = look.start_frame_key.split('.').pop() || 'png'
    const base = `${avatar.name}-${look.name || 'look'}`
      .trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    const link = document.createElement('a')
    link.href = url
    link.download = `${base || 'look'}.${extension}`
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  useEffect(() => {
    if (!viewLookID) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') moveViewer(-1)
      if (event.key === 'ArrowRight') moveViewer(1)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })

  const removeLook = useMutation({
    mutationFn: (lookID: string) =>
      api.del(`/api/workspaces/${ws}/avatars/${avatar.id}/looks/${lookID}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['looks', ws, avatar.id] }),
  })

  const retryLook = useMutation({
    mutationFn: (taskID: string) => api.post<LookTask>(
      `/api/workspaces/${ws}/tasks/${taskID}/retry-look`, {},
    ),
    onSuccess: task => {
      qc.setQueryData<LookTask[]>(['tasks', ws], current => [
        task,
        ...(current ?? []).filter(item => item.id !== retryLook.variables),
      ])
      qc.invalidateQueries({ queryKey: ['tasks', ws] })
    },
  })

  return (
    <Card className="group space-y-4">
      <div className="flex gap-4">
        {/* The face is the product. It gets real space rather than a chip. */}
        <AuthImage
          ws={ws} storageKey={avatar.closeup_key} alt={avatar.name}
          ratio="portrait" className="w-24 shrink-0" thumbnail
        />
        <div className="min-w-0 flex-1">
          <CardTitle>{avatar.name}</CardTitle>
          {cleanupTask && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-ink-faint">
              <Loader2 className="size-3 animate-spin text-primary" /> Preparing identity image…
            </div>
          )}
          {/* Truncated rather than wrapped: the armed delete confirm is wide,
              and a two-line voice ID reflowing the header under it looks like
              a layout fault at the exact moment attention should be on the
              confirm. */}
          <p className="mt-1 truncate font-mono text-xs text-ink-faint">
            voice {avatar.voice_id.slice(0, 12)}…
          </p>
          <p className="mt-1 text-xs text-ink-faint">
            {genderOptions.find(option => option.value === avatar.gender)?.label ?? 'Gender not set'}
            {' · '}
            {ageRangeOptions.find(option => option.value === avatar.age_range)?.label ?? 'Age range not set'}
          </p>
          <p className="mt-1 text-xs text-ink-faint">
            Created {new Date(avatar.created_at).toLocaleDateString(undefined, {
              day: 'numeric', month: 'short', year: 'numeric',
            })}
          </p>
          {!expanded && (
            <p className="mt-1 text-xs text-ink-faint">
              {lookCount} {lookCount === 1 ? 'look' : 'looks'}
              {pendingCount ? ` · ${pendingCount} working` : ''}
            </p>
          )}
        </div>
        <div className="flex shrink-0 self-stretch flex-col items-end justify-between gap-2">
          {canEdit && (
            <div className="flex items-start gap-1">
              {avatar.generation_prompt && (
                <Button
                  variant="ghost" size="icon" title="View generation prompt"
                  onClick={() => setAvatarPromptOpen(true)}
                  className="pointer-events-none opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
                >
                  <FileText />
                </Button>
              )}
              <Button
                variant="ghost" size="icon" title={`Edit ${avatar.name}`}
                onClick={() => setEditingDetails(true)}
                className="pointer-events-none opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
              >
                <Pencil />
              </Button>
              {/* The confirm names what goes with it: the photograph, its looks,
                  and the files behind them. The looks were paid for. */}
              <ConfirmButton
                size="icon"
                title={`Delete ${avatar.name}`}
                confirmLabel={
                  remove.isPending ? 'Deleting…'
                    : lookCount ? `Delete ${avatar.name} and ${lookCount} ${lookCount === 1 ? 'look' : 'looks'}`
                      : `Delete ${avatar.name}`
                }
                disabled={remove.isPending}
                onConfirm={() => remove.mutate()}
                className="pointer-events-none shrink-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
              >
                <Trash2 />
              </ConfirmButton>
            </div>
          )}
          {!expanded && (
            <Button
              variant="secondary"
              size="sm"
              className="bg-transparent hover:bg-surface-2"
              onClick={() => setExpanded(true)}
              aria-expanded={false}
              aria-controls={`avatar-looks-${avatar.id}`}
            >
              View Looks <ChevronDown />
            </Button>
          )}
        </div>
      </div>

      {editingDetails && (
        <EditAvatar
          ws={ws} avatar={avatar}
          onClose={() => setEditingDetails(false)}
          onDone={() => {
            setEditingDetails(false)
            qc.invalidateQueries({ queryKey: ['avatars', ws] })
          }}
        />
      )}

      {/* The server refuses while ads reference the avatar, and says how many.
          That reason is the useful part, so it is shown rather than swallowed. */}
      <ErrorText error={remove.error} />
      <ErrorText error={removeLook.error} />

      {expanded && (
        <div id={`avatar-looks-${avatar.id}`} className="space-y-2">
        <div className="flex items-center justify-between">
          <SectionLabel>
            Looks · {lookCount}{pendingCount ? ` · ${pendingCount} working` : ''}
          </SectionLabel>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => setAddLook(v => !v)}>
              {addLook ? 'Cancel' : <><Plus /> New look</>}
            </Button>
          )}
        </div>

        {looks.data?.length === 0 && !activeLookTasks.length && !failedLookTasks.length && !addLook && (
          <p className="text-xs leading-relaxed text-ink-faint">
            A look is one appearance plus environment — it supplies the start frame, mic,
            space and ambience a video generates from.
          </p>
        )}

        {(!!looks.data?.length || !!activeLookTasks.length || !!failedLookTasks.length) && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-2.5">
            {activeLookTasks.map(task => (
              <figure key={task.id} className="space-y-1.5">
                <div className="flex aspect-[2/3] w-full items-center justify-center overflow-hidden rounded-lg border border-line bg-surface-subtle">
                  <div className="flex flex-col items-center gap-2 text-ink-faint">
                    <Loader2 className="size-5 animate-spin text-primary" />
                    <span className="text-[11px] font-medium">
                      {task.status === 'queued' ? 'Waiting' : 'Working'}
                    </span>
                  </div>
                </div>
                <figcaption>
                  <div className="truncate text-xs font-medium">
                    {task.input.name || 'Generating look'}
                  </div>
                  <div className="truncate text-[11px] text-ink-faint">Saved automatically</div>
                </figcaption>
              </figure>
            ))}
            {failedLookTasks.map(task => (
              <figure key={task.id} className="space-y-1.5">
                <div className="flex aspect-[2/3] w-full items-center justify-center overflow-hidden rounded-lg border border-danger/30 bg-danger/5">
                  <div className="flex flex-col items-center gap-2 px-2 text-center text-danger">
                    <Tooltip.Provider delayDuration={100}>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <button
                            type="button"
                            aria-label="View generation error"
                            className="cursor-default rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40"
                          >
                            <TriangleAlert className="size-5" />
                          </button>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="top"
                            sideOffset={8}
                            className="z-[80] max-w-80 whitespace-normal break-words rounded-[--radius-control] border border-line bg-surface-1 px-3 py-2 text-left text-xs leading-relaxed text-ink shadow-[0_12px_36px_oklch(0_0_0/0.5)]"
                          >
                            {task.error || 'Look generation failed'}
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-1"
                        disabled={retryLook.isPending}
                        onClick={() => retryLook.mutate(task.id)}
                      >
                        <RotateCcw className={retryLook.isPending && retryLook.variables === task.id
                          ? 'animate-spin' : ''} />
                        {retryLook.isPending && retryLook.variables === task.id ? 'Retrying…' : 'Retry'}
                      </Button>
                    )}
                  </div>
                </div>
                <figcaption>
                  <div className="truncate text-xs font-medium">{task.input.name || 'Look failed'}</div>
                  <div className="truncate text-[11px] text-danger">{task.error || 'Try again'}</div>
                </figcaption>
              </figure>
            ))}
            {(looks.data ?? []).map(l => (
              <figure key={l.id} className="space-y-1.5" title={l.ambience ?? ''}>
                <button
                  type="button"
                  onClick={() => setViewLookID(l.id)}
                  className="group/look relative block w-full rounded-[--radius-control] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  title={`View ${l.name || 'look'} larger`}
                >
                  <AuthImage
                    ws={ws} storageKey={l.start_frame_key} alt={l.name ?? 'look'}
                    ratio="portrait" className="w-full transition-opacity group-hover/look:opacity-90" thumbnail
                  />
                  <span className="pointer-events-none absolute right-2 top-2 grid size-7 place-items-center rounded-full bg-black/65 text-white opacity-0 shadow-sm transition-opacity group-hover/look:opacity-100 group-focus-visible/look:opacity-100">
                    <Maximize2 className="size-3.5" />
                  </span>
                </button>
                <figcaption className="flex items-start gap-1">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">{l.name || l.id.slice(0, 6)}</div>
                    <div className="truncate text-[11px] text-ink-faint">{l.mic} · {l.space}</div>
                  </div>
                  <Menu
                    className="-mt-1 shrink-0"
                    label={`Actions for ${l.name || 'this look'}`}
                    actions={[
                      {
                        label: 'Download',
                        icon: Download,
                        onSelect: () => { void downloadLook(l) },
                      },
                      ...(canEdit && l.prompt ? [{
                          label: 'View prompt',
                          icon: FileText,
                          onSelect: () => setPromptLook(l),
                      }] : []),
                      ...(canEdit ? [{
                          label: 'Delete',
                          icon: Trash2,
                          danger: true,
                          disabled: removeLook.isPending,
                          confirmLabel: 'Delete this look permanently?',
                          onSelect: () => removeLook.mutate(l.id),
                      }] : []),
                    ]}
                  />
                </figcaption>
              </figure>
            ))}
          </div>
        )}
        </div>
      )}

      {expanded && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            className="bg-transparent hover:bg-surface-2"
            onClick={() => setExpanded(false)}
            aria-expanded
            aria-controls={`avatar-looks-${avatar.id}`}
          >
            <ChevronUp /> Hide Looks
          </Button>
        </div>
      )}

      {promptLook?.prompt && (
        <Dialog open onOpenChange={open => { if (!open) setPromptLook(null) }} title={promptLook.name || 'Look prompt'}>
          <Field label="Prompt used">
            <Textarea value={promptLook.prompt} readOnly rows={12} className="resize-none text-ink-dim" />
          </Field>
        </Dialog>
      )}
      {avatarPromptOpen && avatar.generation_prompt && (
        <Dialog open onOpenChange={open => { if (!open) setAvatarPromptOpen(false) }} title={`${avatar.name} generation prompt`}>
          <div className="space-y-4">
            <Textarea value={avatar.generation_prompt} readOnly rows={14} className="resize-none text-ink-dim" />
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setAvatarPromptOpen(false)}>Close</Button>
            </div>
          </div>
        </Dialog>
      )}

      {viewedLook && (
        <Dialog
          open
          onOpenChange={open => { if (!open) setViewLookID(null) }}
          title={viewedLook.name || 'Look'}
        >
          <div className="relative flex min-h-64 items-center justify-center">
            <AuthImage
              ws={ws}
              storageKey={viewedLook.start_frame_key}
              alt={viewedLook.name ?? 'look'}
              ratio="free"
              className="mx-auto max-h-[68vh] w-auto max-w-full"
            />
            {savedLooks.length > 1 && (
              <>
                <div className="absolute left-2 top-1/2 -translate-y-1/2">
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => moveViewer(-1)}
                    className="rounded-full bg-surface-1/90 shadow-lg active:translate-y-0"
                    aria-label="Previous look"
                    title="Previous look"
                  >
                    <ChevronLeft />
                  </Button>
                </div>
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => moveViewer(1)}
                    className="rounded-full bg-surface-1/90 shadow-lg active:translate-y-0"
                    aria-label="Next look"
                    title="Next look"
                  >
                    <ChevronRight />
                  </Button>
                </div>
              </>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between gap-4 text-xs text-ink-faint">
            <span>{viewedLookIndex + 1} of {savedLooks.length}</span>
            <span className="truncate">{viewedLook.mic} · {viewedLook.space}</span>
          </div>
          <LookClassification look={viewedLook} />
        </Dialog>
      )}

      {addLook && (
        <NewLook
          ws={ws} avatarID={avatar.id}
          onDone={() => {
            setAddLook(false)
            qc.invalidateQueries({ queryKey: ['tasks', ws] })
          }}
          onClose={() => setAddLook(false)}
        />
      )}
    </Card>
  )
}

const classificationLabel = (value: string | null) => value
  ? value.replaceAll('_', ' ').replace(/^./, c => c.toUpperCase())
  : 'Unclear'

function confidence(value: number | null) {
  return value == null ? '' : ` · ${Math.round(value * 100)}%`
}

function LookClassification({ look }: { look: Look }) {
  const status = look.classification_status
  return (
    <section className="mt-4 rounded-[--radius-control] border border-line bg-surface-2 p-3">
      <h3 className="text-xs font-semibold text-ink">Visual classification</h3>
      {!status && <p className="mt-2 text-xs text-ink-faint">Not classified</p>}
      {status === 'pending' && <p className="mt-2 text-xs text-ink-faint">Classifying…</p>}
      {status === 'failed' && (
        <p className="mt-2 text-xs text-ink-faint">Classification unavailable</p>
      )}
      {status === 'classified' && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
          <dt className="text-ink-faint">Camera</dt>
          <dd className="text-ink-dim">{classificationLabel(look.camera_ownership)}{confidence(look.camera_ownership_confidence)}</dd>
          <dt className="text-ink-faint">Framing</dt>
          <dd className="text-ink-dim">{classificationLabel(look.framing)}{confidence(look.framing_confidence)}</dd>
          <dt className="text-ink-faint">Posture</dt>
          <dd className="text-ink-dim">{classificationLabel(look.posture)}</dd>
          <dt className="text-ink-faint">Left hand</dt>
          <dd className="text-ink-dim">{classificationLabel(look.left_hand_state)}{confidence(look.left_hand_confidence)}</dd>
          <dt className="text-ink-faint">Right hand</dt>
          <dd className="text-ink-dim">{classificationLabel(look.right_hand_state)}{confidence(look.right_hand_confidence)}</dd>
          <dt className="text-ink-faint">Environment</dt>
          <dd className="text-ink-dim">{classificationLabel(look.environment_type)}{confidence(look.environment_confidence)}</dd>
          <dt className="text-ink-faint">Occupancy</dt>
          <dd className="text-ink-dim">{classificationLabel(look.occupancy)}{confidence(look.occupancy_confidence)}</dd>
          <dt className="text-ink-faint">Wind exposure</dt>
          <dd className="text-ink-dim">{classificationLabel(look.wind_exposure)}{confidence(look.wind_exposure_confidence)}</dd>
          <dt className="text-ink-faint">Visible mic</dt>
          <dd className="text-ink-dim">{classificationLabel(look.visible_mic)}{confidence(look.visible_mic_confidence)}</dd>
        </dl>
      )}
      <div className="mt-3 border-t border-line pt-3 text-xs">
        <p className="font-semibold text-ink">Audio treatment</p>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt className="text-ink-faint">Microphone</dt>
          <dd className="text-ink-dim">{classificationLabel(look.mic)}</dd>
          <dt className="text-ink-faint">Space</dt>
          <dd className="text-ink-dim">{classificationLabel(look.space)}</dd>
          <dt className="text-ink-faint">Ambience bed</dt>
          <dd className="text-ink-dim">{!look.ambience ? 'Not selected' : look.ambience_cached ? 'Cached and reusable' : 'Created on first finished video'}</dd>
        </dl>
        <p className="mt-2 leading-relaxed text-ink-faint">{look.ambience || 'No ambience selected'}</p>
      </div>
      {look.classification_attempted_at && (
        <p className="mt-2 text-[11px] text-ink-faint">
          {status === 'classified' ? 'AI classified' : 'AI classification attempted'}{' '}
          {new Date(look.classification_attempted_at).toLocaleString()}
        </p>
      )}
    </section>
  )
}

function EditAvatar({ ws, avatar, onDone, onClose }: {
  ws: string
  avatar: Avatar
  onDone: () => void
  onClose: () => void
}) {
  const [name, setName] = useState(avatar.name)
  const [gender, setGender] = useState(avatar.gender ?? '')
  const [ageRange, setAgeRange] = useState(avatar.age_range ?? '')
  const [voiceID, setVoiceID] = useState(avatar.voice_id)

  const update = useMutation({
    mutationFn: () => api.patch(`/api/workspaces/${ws}/avatars/${avatar.id}`, {
      name: name.trim(), gender, age_range: ageRange, voice_id: voiceID,
    }),
    onSuccess: onDone,
  })

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose() }} title={`Edit ${avatar.name}`}>
      <div className="space-y-6">
        <Step n={1} title="The person">
          <div className="flex items-start gap-4">
            <AuthImage
              ws={ws} storageKey={avatar.source_key || avatar.closeup_key}
              alt={avatar.name} ratio="portrait" className="w-24 shrink-0"
            />
            <p className="max-w-md text-xs leading-relaxed text-ink-faint">
              The original photo defines this person and every saved look is built from it.
              To use a different person or photo, create a new avatar so existing videos and looks stay consistent.
            </p>
          </div>
        </Step>

        <Step n={2} title="Details">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Name">
              <Input value={name} onChange={e => setName(e.target.value)} />
            </Field>
            <Field label="Gender">
              <Select value={gender} onValueChange={setGender} placeholder="Select gender" items={genderOptions} />
            </Field>
            <Field label="Age range">
              <Select value={ageRange} onValueChange={setAgeRange} placeholder="Select age range" items={ageRangeOptions} />
            </Field>
          </div>
        </Step>

        <Step
          n={3} title="Voice"
          hint="Belongs to the person, not the setting — the same voice is used wherever they are filmed."
        >
          <VoicePicker ws={ws} value={voiceID} onChange={setVoiceID} />
        </Step>

        <ErrorText error={update.error} />
        <div className="flex gap-2 border-t border-line pt-4">
          <Button
            variant="primary" disabled={!name.trim() || !gender || !ageRange || !voiceID || update.isPending}
            onClick={() => update.mutate()}
          >
            {update.isPending ? 'Saving…' : 'Save avatar'}
          </Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Dialog>
  )
}

function NewAvatar({ ws, onDone, onClose }: {
  ws: string
  onDone: () => void
  onClose: () => void
}) {
  const [method, setMethod] = useState('upload')
  const [name, setName] = useState('')
  const [gender, setGender] = useState('')
  const [ageRange, setAgeRange] = useState('')
  const [voiceID, setVoiceID] = useState('')
  const [sourceKey, setSourceKey] = useState('')

  const generated = useGeneration<{ source_key: string; prompt: string }>(ws, 'avatars/generate')
  const generatedKey = generated.result?.source_key ?? ''
  const selectedKey = method === 'ai' ? generatedKey : sourceKey

  const draftAppearance = useMutation({
    mutationFn: () => api.post<{ appearance: string }>(`/api/workspaces/${ws}/avatars/draft`, {
      gender, age_range: ageRange, appearance: sourceKey.trim(),
    }),
    onSuccess: result => setSourceKey(result.appearance),
  })

  const create = useMutation({
    mutationFn: () =>
      api.post(`/api/workspaces/${ws}/avatars`, {
        name: name.trim(), gender, age_range: ageRange, voice_id: voiceID,
        source_key: selectedKey, ai_generated: method === 'ai',
        generation_prompt: method === 'ai' ? generated.result?.prompt : undefined,
      }),
    onSuccess: () => { generated.reset(); onDone() },
  })

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose() }} title="New avatar">
      <div className="space-y-6">

      <Tabs
        value={method}
        onValueChange={setMethod}
        tabs={[
          { value: 'upload', label: 'Upload a person' },
          { value: 'ai', label: 'Create with AI' },
        ]}
      />

      <Step n={1} title="The person">
        {method === 'upload' ? (
          <div className="grid items-stretch gap-3 sm:grid-cols-2">
            <FilePicker
              ws={ws} category="source" accept="image/*" label="Upload file"
              value={sourceKey} onChange={setSourceKey}
              hint="Use a clear, ordinary face photo. We’ll lightly clean it in the background and keep the original as a fallback."
            />
            {!sourceKey && <PhoneImageUpload ws={ws} category="source" onUploaded={setSourceKey} />}
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_15rem]">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Gender">
                  <Select value={gender} onValueChange={setGender} placeholder="Select gender" items={genderOptions} />
                </Field>
                <Field label="Age range">
                  <Select value={ageRange} onValueChange={setAgeRange} placeholder="Select age range" items={ageRangeOptions} />
                </Field>
              </div>
              <Field help="Describe distinguishing features and hair. The setting and image realism are handled automatically.">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-[13px] font-medium text-ink-dim">What should this person look like?</label>
                  <Button
                    variant="ghost" size="sm"
                    disabled={!gender || !ageRange || draftAppearance.isPending || generated.busy}
                    onClick={() => draftAppearance.mutate()}
                  >
                    {draftAppearance.isPending
                      ? <><Loader2 className="animate-spin" /> Drafting…</>
                      : <><Sparkles /> {sourceKey.trim() ? 'Regenerate description' : 'Generate description'}</>}
                  </Button>
                </div>
                <Textarea
                  value={sourceKey}
                  onChange={e => setSourceKey(e.target.value)}
                  rows={4}
                  maxLength={800}
                  placeholder="Example: Friendly creator with short dark curly hair, light stubble and an approachable everyday style."
                />
              </Field>
              <ErrorText error={draftAppearance.error} />
              <Button
                variant="secondary"
                disabled={!gender || !ageRange || generated.busy}
                onClick={() => generated.submit.mutate({
                  gender, age_range: ageRange, appearance: sourceKey.trim(),
                })}
              >
                {generated.busy ? <><Loader2 className="animate-spin" /> Generating…</> : <><Sparkles /> {generatedKey ? 'Generate another' : 'Generate avatar'}</>}
              </Button>
              {!gender || !ageRange ? <p className="text-xs text-ink-faint">Choose gender and age range first.</p> : null}
              <ErrorText error={generated.error || generated.failed} />
            </div>
            <div className="overflow-hidden rounded-[--radius-card] border border-line bg-surface-2">
              {generatedKey ? (
                <AuthImage ws={ws} storageKey={generatedKey} alt="Generated avatar" ratio="portrait" className="h-full min-h-64 w-full" />
              ) : (
                <div className="grid min-h-64 place-items-center px-5 text-center text-xs text-ink-faint">
                  Your generated identity photo will appear here.
                </div>
              )}
            </div>
          </div>
        )}
      </Step>

      {(method === 'upload' || generatedKey) && <Step n={2} title="Details">
        <div className={`grid max-w-2xl gap-4 ${method === 'upload' ? 'sm:grid-cols-3' : ''}`}>
          <Field label="Name">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Emma" />
          </Field>
          {method === 'upload' && <>
            <Field label="Gender">
              <Select value={gender} onValueChange={setGender} placeholder="Select gender" items={genderOptions} />
            </Field>
            <Field label="Age range">
              <Select value={ageRange} onValueChange={setAgeRange} placeholder="Select age range" items={ageRangeOptions} />
            </Field>
          </>}
        </div>
      </Step>}

      {(method === 'upload' || generatedKey) && <Step
        n={3} title="Voice"
        hint="Belongs to the person, not the setting — the same voice is used wherever they are filmed."
      >
        <VoicePicker ws={ws} value={voiceID} onChange={setVoiceID} />
      </Step>}

      {(method === 'upload' || generatedKey) && <>
        <ErrorText error={create.error} />
        <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
        <Button
          variant="primary" onClick={() => create.mutate()}
          disabled={!selectedKey || !name.trim() || !gender || !ageRange || !voiceID || create.isPending || generated.busy}
        >
          {create.isPending ? 'Saving…' : 'Save avatar'}
        </Button>
        {/* Says which step is outstanding rather than leaving a disabled
            button with no explanation. */}
        {!create.isPending && (!selectedKey || !name.trim() || !gender || !ageRange || !voiceID) && (
          <span className="text-xs text-ink-faint">
            {!selectedKey ? (method === 'ai' ? 'Generate an avatar photo' : 'Choose a photo')
              : !name.trim() ? 'Give the avatar a name'
                : !gender ? 'Choose a gender'
                  : !ageRange ? 'Choose an age range'
                : 'Choose a voice'}
          </span>
        )}
        </div>
      </>}
      </div>
    </Dialog>
  )
}

function NewLook({
  ws, avatarID, onDone, onClose,
}: {
  ws: string
  avatarID: string
  onDone: () => void
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [name] = useState('')
  const [envKey, setEnvKey] = useState('')
  const [environmentMode, setEnvironmentMode] = useState('reference')
  const [quantity, setQuantity] = useState('5')
  const [selectedConcepts, setSelectedConcepts] = useState<string[]>([])
  const [preferences, setPreferences] = useState('')
  const [sameOutfit, setSameOutfit] = useState(true)
  const [useExtraInstructions, setUseExtraInstructions] = useState(false)
  const [outfit, setOutfit] = useState('')
  const [extra, setExtra] = useState('')
  const isAIMode = environmentMode === 'ai'

  useEffect(() => {
	setSelectedConcepts(current => current.slice(0, Number(quantity)))
  }, [quantity])
  const concepts = useQuery({
    queryKey: ['look-concepts', ws],
    queryFn: () => api.get<LookConcept[]>(`/api/workspaces/${ws}/look-concepts`),
    enabled: isAIMode,
  })

  const generate = useMutation({
    mutationFn: async () => {
      if (isAIMode) {
        return api.post<LookTask[]>(
          `/api/workspaces/${ws}/avatars/${avatarID}/looks/generate-bulk`, {
            quantity: Number(quantity), concept_ids: selectedConcepts, preferences: preferences.trim(),
          },
        )
      }
      const task = await api.post<LookTask>(
        `/api/workspaces/${ws}/avatars/${avatarID}/looks/generate`, {
          env_key: environmentMode === 'reference' ? envKey : '',
          environment: '',
          name: name.trim(),
          outfit: !sameOutfit ? outfit.trim() : '', props: '',
          extra: useExtraInstructions ? extra.trim() : '', mic: '', space: '', ambience: '',
          camera_style: 'match_frame', speaking_to: 'match_frame', posture: 'match_frame',
        },
      )
      return [task]
    },
    onSuccess: accepted => {
      // Put the receipts into the shared task cache before closing. The avatar
      // shows all placeholders in the very next render; the network refresh
      // then reconciles them with any other workspace activity.
      qc.setQueryData<LookTask[]>(['tasks', ws], current => {
        const acceptedIDs = new Set(accepted.map(task => task.id))
        return [...accepted, ...(current ?? []).filter(task => !acceptedIDs.has(task.id))]
      })
      onDone()
    },
  })

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose() }} title="New look" closeDisabled={generate.isPending}>
    <div className="space-y-4">

      <div className="space-y-3">
        <SectionLabel>Environment</SectionLabel>
        <Tabs
          value={environmentMode}
          onValueChange={setEnvironmentMode}
          tabs={[
            { value: 'reference', label: 'Upload reference' },
            { value: 'ai', label: 'AI-generated' },
          ]}
        />
        {environmentMode === 'reference' ? (
          <div className="space-y-4">
            <div className="grid items-stretch gap-3 sm:grid-cols-2">
              <FilePicker
                ws={ws} category="environment" accept="image/*" label="Upload file"
                value={envKey} onChange={setEnvKey}
                hint="A screenshot of a real social video works best. Its setting, lighting and camera character will be carried into the look."
              />
              {!envKey && <PhoneImageUpload ws={ws} category="environment" onUploaded={setEnvKey} />}
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={sameOutfit}
                onChange={e => setSameOutfit(e.target.checked)}
                className="size-4 rounded border-line accent-primary"
              />
              Keep avatar’s original outfit
            </label>
            {!sameOutfit && (
              <Field label="New outfit instructions" help="Describe what the avatar should wear in this look.">
                <Input
                  value={outfit}
                  onChange={e => setOutfit(e.target.value)}
                  placeholder="a plain black sleeveless top"
                />
              </Field>
            )}
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={useExtraInstructions}
                onChange={e => setUseExtraInstructions(e.target.checked)}
                className="size-4 rounded border-line accent-primary"
              />
              Extra instructions
            </label>
            {useExtraInstructions && (
              <Field help="Optional direction for pose, gaze, expression, lighting or composition.">
                <Input
                  value={extra}
                  onChange={e => setExtra(e.target.value)}
                  placeholder="Angle them slightly toward someone off camera"
                />
              </Field>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Field label="How many looks?">
              <Select value={quantity} onValueChange={setQuantity} items={['1', '5', '10'].map(v => ({ value: v, label: v }))} />
            </Field>
            <div className="space-y-3">
              <div>
                <div className="text-[13px] font-medium text-ink-dim">Choose concepts</div>
                <p className="text-xs text-ink-faint">Choose one or more. Selected concepts repeat evenly when needed.</p>
              </div>
              {concepts.isLoading && <p className="text-sm text-ink-faint">Loading concepts…</p>}
              {Array.from(new Set((concepts.data ?? []).map(c => c.group))).map(group => (
                <div key={group} className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{group}</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(concepts.data ?? []).filter(c => c.group === group).map(concept => {
                      const selected = selectedConcepts.includes(concept.id)
                      return <button key={concept.id} type="button" aria-pressed={selected}
                        onClick={() => setSelectedConcepts(current => selected
                          ? current.filter(id => id !== concept.id)
                          : current.length < Number(quantity) ? [...current, concept.id] : current)}
                        className={`rounded-[--radius-control] border p-3 text-left transition-colors ${selected ? 'border-primary bg-primary/10' : 'border-line bg-surface-2 hover:border-line-strong'}`}>
                        <div className="text-sm font-medium text-ink">{concept.title}</div>
                        <div className="mt-1 text-xs leading-relaxed text-ink-faint">{concept.description}</div>
                      </button>
                    })}
                  </div>
                </div>
              ))}
            </div>
            <Field label="Optional visual preferences" help="Customize clothing, colors, décor, location details, neutral lighting, or non-conflicting props. Filming style, framing, posture, eyeline, hand setup, and microphone arrangement remain unchanged.">
              <Textarea rows={3} value={preferences} onChange={e => setPreferences(e.target.value)} maxLength={800}
                placeholder="Example: dark green shirt, modern home office with plants, soft neutral window light" />
            </Field>
            <ErrorText error={concepts.error} />
          </div>
        )}
      </div>

      <ErrorText error={generate.error} />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={generate.isPending || (environmentMode === 'reference'
            ? !envKey || (!sameOutfit && !outfit.trim())
            : selectedConcepts.length === 0)}
          onClick={() => generate.mutate()}
        >
          <Sparkles /> {generate.isPending
            ? 'Adding to avatar…'
            : isAIMode ? `Generate ${quantity} ${quantity === '1' ? 'look' : 'looks'}` : 'Generate look'}
        </Button>
      </div>
    </div>
    </Dialog>
  )
}
